// React Query hooks for the PlanYear surface.

import {
  useMutation, useQuery, useQueryClient,
} from "@tanstack/react-query";

import {
  archivePlanYear, createPlanYear, fetchPlanYear,
  fetchPlanYearList, restorePlanYear, updatePlanYear,
  type PlanYearCreateInput, type PlanYearListResponse,
  type PlanYearPatchInput, type PlanYearRow,
} from "./planYearApi";


export const PLAN_YEARS_QUERY_KEY = "planYears" as const;


export function usePlanYearListQuery(
  accountId: string | undefined,
  archived: "active" | "archived" | "all" = "all",
) {
  return useQuery<PlanYearListResponse>({
    queryKey: [PLAN_YEARS_QUERY_KEY, "list", accountId, archived],
    queryFn: ({ signal }) =>
      fetchPlanYearList(accountId!, archived, { signal }),
    enabled: Boolean(accountId),
  });
}


export function usePlanYearDetailQuery(
  accountId: string | undefined,
  planYearId: string | undefined,
) {
  return useQuery<PlanYearRow>({
    queryKey: [PLAN_YEARS_QUERY_KEY, "detail", accountId, planYearId],
    queryFn: ({ signal }) =>
      fetchPlanYear(accountId!, planYearId!, { signal }),
    enabled: Boolean(accountId && planYearId),
  });
}


function invalidateLists(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: [PLAN_YEARS_QUERY_KEY, "list"] });
  void qc.invalidateQueries({ queryKey: ["accounts"] });
}


export function useCreatePlanYearMutation(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PlanYearCreateInput) =>
      createPlanYear(accountId, input),
    onSuccess: () => invalidateLists(qc),
  });
}


export function useUpdatePlanYearMutation(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { planYearId: string; values: PlanYearPatchInput }) =>
      updatePlanYear(accountId, vars.planYearId, vars.values),
    onSuccess: (_data, vars) => {
      invalidateLists(qc);
      void qc.invalidateQueries({
        queryKey: [PLAN_YEARS_QUERY_KEY, "detail", accountId, vars.planYearId],
      });
    },
  });
}


export function useArchivePlanYearMutation(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planYearId: string) =>
      archivePlanYear(accountId, planYearId),
    onSuccess: () => invalidateLists(qc),
  });
}


export function useRestorePlanYearMutation(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planYearId: string) =>
      restorePlanYear(accountId, planYearId),
    onSuccess: () => invalidateLists(qc),
  });
}
